/**
 * What follows a transition: the Rollbar step events, and the delayed redirect
 * off the existing-session landing.
 *
 * The step events used to be a `useEffect` keyed on the current step, which
 * meant they re-fired on every render where the state matched rather than once
 * per arrival. Forward-only navigation hid that -- each step was entered once,
 * so an effect on step state and a transition hook were indistinguishable. A
 * back affordance separates them: every back press re-enters a step and the
 * effect emits again, inflating start counts and depressing step conversion
 * with no change to the telemetry itself.
 *
 * Binding emission to the transition instead makes arrival the trigger. The
 * decision of what to emit lives here, as pure functions over the destination
 * and a small piece of per-checkout state, so it can be exercised by the unit
 * suite; the provider owns the emission and the payload, because those need
 * React state this module deliberately does not see.
 *
 * Event NAMES are a published interface -- Rollbar dashboards key on them.
 * They are reproduced here byte-identically from the effect this replaces and
 * must not be edited to match any local naming convention.
 */

import { STEPS, PSEUDO_STATES, OUTCOMES } from './transitions'

/** Emitted once per checkout, on entry. Not a step event. */
export const SIGN_UP_STARTED = 'Sign Up Started'

/**
 * Destination step -> event name. Only these four steps emit on arrival;
 * `authenticate`, `resetPassword` and `upsell` emitted nothing before this
 * change and emit nothing now. `authenticate` in particular is the checkout's
 * initial state rather than a destination, and its event is SIGN_UP_STARTED,
 * which is gated per checkout rather than per arrival.
 */
export const STEP_EVENTS = {
  [STEPS.existingUser]: 'Existing User Redirect',
  [STEPS.selectPlan]: 'Select Plan',
  [STEPS.newUser]: 'New User',
  [STEPS.billing]: 'Billing',
}

/**
 * Events keyed to the OUTCOME rather than the destination.
 *
 * A back press lands on `authenticate`, which deliberately emits no arrival
 * event -- its event is the per-checkout `Sign Up Started`, and re-emitting it
 * is the funnel regression this whole module exists to prevent. So the back
 * press cannot be expressed as a STEP_EVENTS entry: the destination is shared
 * with the checkout reset, and only one of the two is a back press. Keying on
 * the outcome is what separates them.
 *
 * This is the one event the render effect never had, so there is no historic
 * name to preserve. It exists because suppressing every re-entry event would
 * discard the back-navigation signal entirely, on the change that introduces
 * back navigation.
 */
export const BACK_PRESSED = 'Back Pressed'

export const OUTCOME_EVENTS = {
  [OUTCOMES.back]: BACK_PRESSED,
}

/** The event an outcome emits regardless of where it lands. Null for most. */
export const outcomeEventFor = (outcome) => OUTCOME_EVENTS[outcome] ?? null

/**
 * The delayed redirect off the existing-session landing: 10s after the
 * bootstrap routes an OAuth return to `existingUser`, the machine leaves for
 * the app. It is an effect of that one transition, not a row -- it selects no
 * destination itself, it re-enters the table later through the redirect
 * outcome, which is what picks between the three exits.
 */
export const DELAYED_REDIRECT = {
  from: PSEUDO_STATES.bootstrap,
  outcome: OUTCOMES.existingSession,
  delayMs: 10000,
}

/**
 * Queue item kinds. A transition records that an event is OWED; it does not
 * build the payload, because the payload is defined over state the transition
 * has just changed and React has not yet applied. Deciding and delivering are
 * separate for that reason -- see planTelemetry below.
 */
export const QUEUE_KINDS = {
  started: 'started',
  step: 'step',
  outcome: 'outcome',
}

/**
 * Which tracker a planned call belongs to.
 *
 * `step` and `outcome` take the same arguments and differ only in the wire
 * string: the step tracker prefixes its name with `Sign Up Step Started: `,
 * because every step event is one. An outcome event is not -- `Back Pressed`
 * is its own published name -- so routing it through the step tracker put
 * `Sign Up Step Started: Back Pressed` on the dashboards. The split here is
 * what keeps the prefix a property of the tracker rather than of the name.
 */
export const TRACKERS = {
  started: 'signUpStarted',
  step: 'signUpStep',
  outcome: 'signUpEvent',
}

/**
 * The `Sign Up Started` payload, reproduced field-for-field from the effect
 * this replaces -- including the coupon precedence, where three conditional
 * spreads write the same key and the last truthy one wins
 * (`utmCampaign` over `couponCode` over `coupon.code`).
 */
export const buildSignUpStartedPayload = ({
  plan, coupon, couponCode, utmCampaign, utm, testsShown = [],
}) => ({
  sku: plan?.sku,
  ...(coupon && { coupon: coupon?.code }),
  ...(couponCode && { coupon: couponCode }),
  ...(utmCampaign && { coupon: utmCampaign }),
  ...(utm && {
    utmCampaign: utm.campaign,
    utmSource: utm.source,
    utmMedium: utm.medium,
  }),
  ...(testsShown.length ? { testsShown } : {}),
})

/** The step-event payload, likewise unchanged from the effect it replaces. */
export const buildStepPayload = ({ plan, coupon, utm, testsShown = [] }) => ({
  sku: plan?.sku,
  ...(coupon && { coupon: coupon?.code }),
  ...(utm && {
    utmCampaign: utm.campaign,
    utmSource: utm.source,
    utmMedium: utm.medium,
  }),
  ...(testsShown.length ? { testsShown } : {}),
})

/**
 * Turn owed events into the exact tracker calls to make, against one snapshot
 * of the values the payload reads.
 *
 * Returning the calls rather than making them is what lets the suite assert
 * the ARGUMENTS -- the names alone were never the risky part. It is the same
 * split the order-response classifier uses: this decides, the provider performs.
 */
export const planTelemetry = (queue, snapshot) => queue.map((item) => {
  if (item.kind === QUEUE_KINDS.started) {
    return { tracker: TRACKERS.started, args: [buildSignUpStartedPayload(snapshot)] }
  }
  const tracker = item.kind === QUEUE_KINDS.outcome ? TRACKERS.outcome : TRACKERS.step
  return { tracker, args: [item.name, snapshot.user, buildStepPayload(snapshot)] }
})

/**
 * Fresh per-checkout telemetry state: the once-per-checkout gate and the
 * events that checkout still owes.
 *
 * The two live in one object because they share one lifetime. Holding the
 * queue separately -- as provider state, which is what it was -- let it
 * outlive the checkout that queued into it: an event queued before a plan had
 * loaded stayed pending across a checkout reset and was delivered later
 * against the NEXT checkout's payload. Resetting the checkout now resets both
 * in a single assignment, so a future reset path cannot forget half of it.
 *
 * `queue` is a plain array the provider mutates in place. It is delivery
 * machinery that nothing renders, so it is deliberately not React state --
 * see the flush effect in SignUpProvider.
 */
export const createCheckoutTelemetry = () => ({ signUpStarted: false, queue: [] })

/**
 * Entering the checkout. Returns the event to emit and the next state; the
 * event is null on every entry after the first, which is what makes a back
 * press cost nothing.
 *
 * Callers thread the returned state; this function mutates nothing, so the
 * suite can replay a whole checkout without a provider. The spread carries
 * `queue` forward by reference on purpose: entering the checkout is not a new
 * checkout, so the events it already owes must survive the gate flipping.
 */
export const enterCheckout = (state) => (
  state.signUpStarted
    ? { event: null, state }
    : { event: SIGN_UP_STARTED, state: { ...state, signUpStarted: true } }
)

/** Arriving at a destination. Null for a destination that emits nothing. */
export const stepEventFor = (destination) => STEP_EVENTS[destination] ?? null

/** True when this transition schedules the delayed redirect. */
export const schedulesDelayedRedirect = (from, outcome) => (
  from === DELAYED_REDIRECT.from && outcome === DELAYED_REDIRECT.outcome
)
